import { Component, DestroyRef, EventEmitter, inject, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { StrategyItem, SectionView, optimizeCategoryName } from '../../strategy-catalog.models';
import { StrategyColumns } from '@pdqdev/pdq-shared/dist/entities/StrategyCatalog';
import { StrategyOptimizationService } from '../../services/strategy-optimization.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';

@Component({
  selector: 'strategy-bookmarks',
  standalone: false,
  templateUrl: './bookmarks.component.html',
  styleUrl: './bookmarks.component.scss',
})
export class BookmarksComponent implements OnChanges {
  @Input() strategies: StrategyItem[] = [];

  @Output() strategiesChanged = new EventEmitter<StrategyItem[]>();
  @Output() strategyClicked = new EventEmitter<StrategyItem>();
  @Output() strategyRemoved = new EventEmitter<string>();

  sectionViews: SectionView[] = [];
  defaultSectionView: SectionView | null = null;
  customSectionViews: SectionView[] = [];
  isEmpty = true;
  connectedDropLists: string[] = [];
  isAddingSection = false;
  newSectionName = '';
  newSectionControl = new FormControl('');

  readonly StrategyColumns = StrategyColumns;
  readonly optimizeCategoryName = optimizeCategoryName;
  readonly sectionDropPredicate = (drag: CdkDrag) =>
    drag.dropContainer.element.nativeElement.classList.contains('sections-list');

  private destroyRef = inject(DestroyRef);

  constructor(
    private strategyOptimizationService: StrategyOptimizationService,
    private toastService: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['strategies']) {
      this.buildSectionViews();
    }
  }

  onStrategyClicked(item: StrategyItem): void {
    this.strategyClicked.emit(item);
  }

  onStrategyRemoved(strategyId: string): void {
    this.strategyRemoved.emit(strategyId);
  }

  onSectionDeleted(sectionId: number): void {
    this.strategyOptimizationService
      .deleteSection(sectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.buildSectionViews();
          this.emitAllStrategies();
        },
        error: () => this.showError('Failed to delete section'),
      });
  }

  onSectionRenamed(event: { id: number; name: string }): void {
    this.strategyOptimizationService
      .renameSection(event.id, event.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.buildSectionViews(),
        error: () => this.showError('Failed to rename section'),
      });
  }

  onOrderChanged(event: { sectionId: number; strategyIds: string[] }): void {
    this.strategyOptimizationService
      .updateSectionOrder(event.sectionId, event.strategyIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => this.showError('Failed to update order'),
      });
  }

  onStrategyTransferred(event: {
    strategyId: string;
    fromSectionId: number;
    toSectionId: number;
    toIndex: number;
  }): void {
    this.strategyOptimizationService
      .moveStrategyBetweenSections(event.strategyId, event.fromSectionId, event.toSectionId, event.toIndex)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.buildSectionViews(),
        error: () => this.showError('Failed to move strategy'),
      });
  }

  onSectionDrop(event: CdkDragDrop<SectionView[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.customSectionViews, event.previousIndex, event.currentIndex);
    const allSectionIds = [
      ...(this.defaultSectionView ? [this.defaultSectionView.section.id] : []),
      ...this.customSectionViews.map((sv) => sv.section.id),
    ];
    this.strategyOptimizationService
      .reorderSections(allSectionIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => this.showError('Failed to reorder sections'),
      });
  }

  startAddingSection(): void {
    this.isAddingSection = true;
    this.newSectionControl.setValue('');
  }

  cancelAddingSection(): void {
    this.isAddingSection = false;
    this.newSectionControl.setValue('');
  }

  confirmAddSection(): void {
    const name = (this.newSectionControl.value || '').trim();
    if (name) {
      this.strategyOptimizationService
        .createSection(name)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.buildSectionViews(),
          error: () => this.showError('Failed to create section'),
        });
    }
    this.isAddingSection = false;
    this.newSectionControl.setValue('');
  }

  onAddSectionKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.confirmAddSection();
    } else if (event.key === 'Escape') {
      this.cancelAddingSection();
    }
  }

  private buildSectionViews(): void {
    const strategyMap = new Map<string, StrategyItem>();
    for (const strategy of this.strategies) {
      strategyMap.set(strategy[StrategyColumns.ID], strategy);
    }

    const sections = this.strategyOptimizationService.getSections();
    this.sectionViews = sections.map((section) => ({
      section,
      strategies: section.strategyIds
        .map((id) => strategyMap.get(id))
        .filter((s): s is StrategyItem => s !== undefined),
    }));

    this.defaultSectionView = this.sectionViews.find((sv) => sv.section.isDefault) ?? null;
    this.customSectionViews = this.sectionViews.filter((sv) => !sv.section.isDefault);
    this.isEmpty = this.sectionViews.every((sv) => sv.strategies.length === 0);
    this.connectedDropLists = this.sectionViews.map((sv) => 'section-' + sv.section.id);
  }

  private showError(message: string): void {
    this.toastService.showToast({ message, mode: 'danger' });
  }

  private emitAllStrategies(): void {
    const allStrategies = this.sectionViews.flatMap((sv) => sv.strategies);
    this.strategiesChanged.emit(allStrategies);
  }
}
